import React from "react";
import { IMobileOrchestrator } from "@nju/data-access-portal-orchestrator";
import { getPaymentErrorCategoryMessage } from "@nju/feature-portal-payment";
import { IconName } from "@nju/icons";
import { BlockText, Separator } from "@nju/ui";
import { formatDate } from "@nju/utils";

interface IStatusProps {
  iconName: IconName;
  title: string;
  text: string | React.ReactNode;
  statusDetailsText?: string;
  statusDetailsIcon?: IconName;
}

interface IGetMobileStatusDataProps {
  paymentErrorCategory: IMobileOrchestrator["paymentErrorCategory"];
  state: IMobileOrchestrator["state"];
  shippingStatus: IMobileOrchestrator["shippingStatus"];
  isUserVerified: boolean | undefined;
  processId: string | undefined;
  identityMethodId: IMobileOrchestrator["identityMethodId"];
  orderSimNumber: string | undefined;
  mnp: IMobileOrchestrator["mnp"];
}

export function getMobileStatusData({
  paymentErrorCategory,
  state,
  shippingStatus,
  isUserVerified,
  processId,
  identityMethodId,
  orderSimNumber,
  mnp,
}: IGetMobileStatusDataProps): IStatusProps | undefined {
  const activationText = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Wszystko gotowe. Aktywuj nju subskrypcja i zacznij korzystać z usługi.
      </BlockText>
      <BlockText
        className="mt-5"
        size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
      >
        Pamiętaj, żeby aktywować usługę w ciągu 48 h - jeśli tego nie zrobisz,
        Twoje zlecenie zostanie anulowane.
      </BlockText>
      <Separator size="xs" tone="invisible" />
    </>
  );

  const mnpWrongUserData = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Podane przez Ciebie dane osobowe są inne niż u Twojego obecnego
        operatora. Wyjaśnij sprawę.
      </BlockText>
      <Separator size="m" tone="invisible" />
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        W każdym momencie możesz anulować obecne zamówienie i złożyć nowe.
      </BlockText>
      <Separator size="xs" tone="invisible" />
    </>
  );

  const mnpNumberInactive = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Wskazany przez Ciebie numer jest nieaktywny. Wyjaśnij sprawęu obecnego
        operatora.
      </BlockText>
      <Separator size="m" tone="invisible" />
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        W każdym momencie możesz anulować obecne zamówienie i złożyć nowe.
      </BlockText>
      <Separator size="xs" tone="invisible" />
    </>
  );

  const mnpTooLongTillTheEndOfTheContract = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Twoja umowa z obecnym operatorem kończy się za ponad 4 miesiące.
        Przeniesienie numeru może wiązać się z dodatkowymi kosztami.
      </BlockText>
      <Separator size="m" tone="invisible" />
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        W każdym momencie możesz anulować obecne zamówienie i złożyć nowe.
      </BlockText>
      <Separator size="xs" tone="invisible" />
    </>
  );

  const mnpGeneralErrorMessage = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Nie możemy przenieść Twojego numeru.
      </BlockText>
      <Separator size="m" tone="invisible" />
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        W każdym momencie możesz anulować obecne zamówienie i złożyć nowe.
      </BlockText>
      <Separator size="xs" tone="invisible" />
    </>
  );

  const shippingPackChan = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Twoja przesyłka z kartą SIM wyruszyła w drogę do Ciebie.
      </BlockText>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Jeśli masz już kartę SIM - dodaj ją teraz.
      </BlockText>
    </>
  );

  const shippingDelvChan = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Twoja przesyłka z kartą SIM została doręczona.
      </BlockText>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Masz już kartę SIM - dodaj ją teraz.
      </BlockText>
    </>
  );

  const mnpContactYourOperator = (
    <>
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Skontaktuj się ze swoim obecnym operatorem i upewnij się co do typu
        oferty (abonament lub na kartę) przypisanego do tego numeru.
      </BlockText>
      <Separator size="m" tone="invisible" />
      <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
        Następnie zleć ponowny transfer do nju, ale wcześniej anuluj obecne
        zamówienie.
      </BlockText>
    </>
  );

  // sample user to invoke this error: portal251+p7@rafal.im
  // https://dop-orange.atlassian.net/wiki/spaces/DGB/pages/1367703621/Mock
  if (state === "ORDER_PAYMENT_ERROR" && paymentErrorCategory) {
    return {
      title: "błąd płatności",
      text: getPaymentErrorCategoryMessage(paymentErrorCategory),
      iconName: "exclamation-circle",
    };
  }

  if (
    (state === "ORDER_COMMITTED" || state === "DOCUMENTS_SENDING") &&
    processId !== "MNP_IN"
  ) {
    return {
      title: "zamówienie złożone",
      text: "Przyjęliśmy Twoje zamówienie i je weryfikujemy - będziemy informować Cię o zmianach statusu.",
      iconName: "shopping-bag",
    };
  }

  if (state === "SIM" && shippingStatus === "PACK_CHAN") {
    return {
      title: "przesyłka gotowa",
      text: shippingPackChan,
      iconName: "truck",
    };
  }

  if (state === "ORDER_FULFILLMENT" && processId !== "MNP_IN") {
    return {
      title: "zamówienie potwierdzone",
      text: "Mamy już wszystko, czego potrzebujemy i szykujemy Twoje zamówienie.",
      iconName: "check-circle",
    };
  }

  if (processId !== "MNP_IN") {
    if (state === "SIM" && shippingStatus === "PACK_CHAN") {
      return {
        title: "przesyłka gotowa",
        text: "Twoja przesyłka z kartą SIM wyruszyła w drogę do Ciebie. Jeśli masz już kartę SIM - dodaj ją teraz.",
        iconName: "truck",
      };
    }

    if (state === "SIM" && shippingStatus === "DELV_PROGR_CHAN") {
      return {
        title: "dostawa w trakcie",
        text: "Twoja przesyłka z kartą SIM już wkrótce zostanie dostarczona. Jeśli masz już kartę SIM - dodaj ją teraz.",
        iconName: "truck",
      };
    }

    if (state === "SIM" && shippingStatus === "DELV_CHAN") {
      return {
        title: "karta SIM dostarczona",
        text: shippingDelvChan,
        iconName: "sim-card",
      };
    }
  }

  if (
    state === "ORDER_FULFILLMENT_FAILED" &&
    (shippingStatus === "RET_CHAN" ||
      shippingStatus === "RET_PROGR_CHAN" ||
      shippingStatus === "CANC_CHAN")
  ) {
    return {
      title: "dostawa nieudana",
      text: "Nie udało nam się dostarczyć Ci karty SIM. Anuluj obecne zamówienie i złóż nowe.",
      iconName: "times-circle",
    };
  }

  if (state === "PAYMENT_METHOD") {
    return {
      title: "wybór metody płatności",
      text: "Przejdź dalej, aby wybrać sposób płatności i dokończyć zamówienie.",
      iconName: "money-bill-wave",
      statusDetailsText: "oczekiwanie na konfigurację metody płatności",
      statusDetailsIcon: "spinner",
    };
  }

  if (
    state === "ACTIVATION" &&
    !isUserVerified &&
    identityMethodId === "TECHNICALPARTNER"
  ) {
    return {
      title: "weryfikacja tożsamości",
      text: "Twoja tożsamość nie została jeszcze potwierdzona. Jeśli nie wiesz, jak potwierdzić tożsamość,  skontaktuj się z nami na czacie.",
      iconName: "sync",
    };
  }

  if (state === "ACTIVATION" && processId === "NEW_NUMBER") {
    return {
      title: "oczekiwanie na aktywację",
      text: "Wszystko gotowe. Aktywuj nju subskrypcja i zacznij korzystać z usługi.",
      iconName: "check-circle",
    };
  }

  if (state === "ACTIVATION" && processId === "LEGACY_MIGRATION") {
    return {
      title: "oczekiwanie na aktywację",
      text: activationText,
      iconName: "check-circle",
    };
  }

  if (state === "ACTIVATION" && processId === "MNP_IN") {
    return {
      title: "oczekiwanie na aktywację",
      text: "Wszystko gotowe. Aktywuj nju subskrypcja i zacznij korzystać z usługi. Pamiętaj, żeby aktywować usługę w ciągu 30 dni, inaczej stracisz numer.",
      iconName: "check-circle",
    };
  }

  if (state === "ORDER_PAYMENT") {
    return {
      title: "oczekiwanie na potwierdzenie płatności",
      text: "Daj nam chwilę. Sprawdzamy, czy płatność została poprawnie zrealizowana.",
      iconName: "sync",
    };
  }

  if (state === "ORDER_FINISHING" || state === "COMPLETED") {
    return {
      title: "realizacja w toku",
      text: "Potrzebujemy jeszcze chwili. Zajmie nam to maksymalnie kilka minut.",
      iconName: "sync",
    };
  }

  if (state === "ORDER_PENDING_CANCELLATION") {
    if (processId === "MNP_IN") {
      return {
        title: "zamówienie w trakcie anulowania",
        text: "Przyjęliśmy zlecenie na anulowanie zamówienia.",
        iconName: "times-circle",
      };
    }

    if (processId !== "MNP_IN") {
      return {
        title: "zamówienie w trakcie anulowania",
        text: "Przyjęliśmy zlecenie na anulowanie zamówienia.",
        iconName: "spinner",
      };
    }
  }

  if (processId === "MNP_IN") {
    if (
      !mnp?.transferDate &&
      (state === "ORDER_COMMITTED" || state === "ORDER_FULFILLMENT")
    ) {
      return {
        title: "sprawdzamy, czy możesz przenieść numer",
        text: "Potwierdzamy możliwość przeniesienia numeru z Twoim operatorem.",
        iconName: "spinner",
        statusDetailsText: "sprawdzamy u Twojego operatora",
      };
    }

    if (
      mnp?.transferDate &&
      (state === "ORDER_COMMITTED" ||
        (state === "ORDER_FULFILLMENT" &&
          (!mnp.state ||
            [
              "Processing",
              "ProposalSent",
              "Rejected",
              "Rejected4Manual",
              "Failed",
            ].includes(mnp.state)) &&
          mnp.npMessageCode !== "E06"))
    ) {
      return {
        title: "sprawdzamy, czy możesz przenieść numer",
        text: `Potwierdzamy możliwość przeniesienia numeru z Twoim operatorem. Planowana data przeniesienia: ${formatDate(
          mnp.transferDate,
          false,
        )}`,
        iconName: "spinner",
        statusDetailsText: "sprawdzamy u Twojego operatora",
      };
    }

    if (
      state === "ORDER_FULFILLMENT" &&
      !orderSimNumber &&
      mnp?.npMessageCode === "E06" &&
      mnp?.transferDate
    ) {
      return {
        title: "przeniesienie numeru potwierdzone",
        text: `Potwierdziliśmy możliwość przeniesienia numeru. Planowana data przeniesienia: ${formatDate(
          mnp.transferDate,
          false,
        )}, między 00:00 a 06:00.`,
        iconName: "check",
      };
    }

    if (
      state === "SIM" &&
      shippingStatus === "PACK_CHAN" &&
      mnp?.transferDate
    ) {
      return {
        title: "przesyłka gotowa",
        text: (
          <>
            <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
              Twoja przesyłka z kartą SIM wyruszyła w drogę do Ciebie. Planowana
              data przeniesienia: {formatDate(mnp.transferDate, false)}
            </BlockText>
            <Separator size="s" tone="invisible" />
            <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
              Jeśli masz już kartę SIM - dodaj ją teraz i aktywuj usługę. Masz
              na to 30 dni. Jeśli tego nie zrobisz Twoje zamówienie zostanie
              anulowane a Ty stracisz wybrany numer.
            </BlockText>
          </>
        ),
        iconName: "sim-card",
      };
    }

    if (state === "SIM" && shippingStatus === "DELV_PROGR_CHAN") {
      return {
        title: "dostawa w trakcie",
        text: "Twoja przesyłka z kartą SIM już wkrótce zostanie dostarczona. Jeśli masz już kartę SIM - dodaj ją teraz.",
        iconName: "truck",
      };
    }

    if (state === "SIM" && shippingStatus === "DELV_CHAN") {
      return {
        title: "karta SIM dostarczona",
        text: shippingDelvChan,
        iconName: "check",
      };
    }

    if (
      state === "ORDER_FULFILLMENT" &&
      ["E06", "E12", "E13", undefined].includes(mnp?.npMessageCode) &&
      orderSimNumber &&
      mnp?.transferDate
    ) {
      return {
        title: "oczekiwanie na przeniesienie",
        text: `Czekamy na przeniesienie numeru od Twojego obecnego operatora. Planowana data przeniesienia to ${formatDate(
          mnp.transferDate,
          false,
        )}, między 00:00 a 06:00. Kiedy już potwierdzimy przeniesienie numeru, wybierzesz sposób płatności (o ile jeszcze nie został wybrany), a gdy Twój numer zostanie już przeniesiony do nju, poprosimy Cię o aktywację usługi.`,
        iconName: "info-circle",
      };
    }

    //MNP Unhappy flow
    if (state === "ORDER_FULFILLMENT_FAILED") {
      if (mnp?.reasonCode === "302" && !orderSimNumber) {
        return {
          title: "przeniesienie numeru niemożliwe",
          text: "Karta SIM nie została dodana, więc nie mogliśmy przenieść Twojego numeru. Sprawdź powód odmowy.",
          iconName: "times-circle",
        };
      }

      if (
        mnp?.npMessageCode !== "E16" &&
        mnp?.npMessageCode !== "E17" &&
        (shippingStatus === "RET_CHAN" ||
          shippingStatus === "RET_PROGR_CHAN" ||
          shippingStatus === "CANC_CHAN")
      ) {
        return {
          title: "dostawa nieudana",
          text: "Nie udało nam się dostarczyć Ci karty SIM. Anuluj obecne zamówienie i złóż nowe.",
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "1" && mnp?.state !== "Failed") {
        return {
          title: "przeniesienie numeru wstrzymane",
          text: mnpWrongUserData,
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "2" && mnp?.state !== "Failed") {
        return {
          title: "przeniesienie numeru wstrzymane",
          text: mnpNumberInactive,
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "3") {
        return {
          title: "przeniesienie numeru wstrzymane",
          text: "Dostaliśmy informację od Twojego operatora, że rezygnujesz z przeniesienia numeru do nju subskrypcja. Jeśli chcesz zmienić swoją decyzję, złóż nowe zamówienie.",
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "4" && mnp?.state === "Rejected") {
        return {
          title: "przeniesienie numeru wstrzymane",
          text: mnpContactYourOperator,
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "6" && mnp?.state !== "Failed") {
        return {
          title: "przeniesienie numeru wstrzymane",
          text: mnpGeneralErrorMessage,
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "8" && mnp?.state !== "Failed") {
        return {
          title: "przeniesienie numeru wstrzymane",
          text: mnpTooLongTillTheEndOfTheContract,
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "109") {
        return {
          title: "przeniesienie numeru niemożliwe",
          text: "Dostaliśmy już wcześniej prośbę o przeniesienie tego numeru, dlatego zamykamy to zlecenie. Złóż nowe zamówienie.",
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "110") {
        return {
          title: "przeniesienie numeru niemożliwe",
          text: "Ten numer jest już w trakcie przenoszenia do innego operatora. Jeśli chcesz przenieść swój numer do nju subskrypcja, anuluj wniosek u innego operatora i złóż nowe zamówienie.",
          iconName: "times-circle",
        };
      }

      if (mnp?.reasonCode === "155") {
        return {
          title: "przeniesienie numeru niemożliwe",
          text: "Przeniesienie Twojego numeru zostało anulowane z przyczyn technicznych. Złóż nowe zamówienie.",
          iconName: "times-circle",
        };
      }

      return {
        title: "przeniesienie numeru niemożliwe",
        text: "Nie możemy przenieść Twojego numeru do nju subskrypcja. Złóż nowe zamówienie.",
        iconName: "times-circle",
      };
    }
  }

  return undefined;
}
